/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from '../../../state'
import { useForm } from 'react-hook-form'
import { Input } from 'src/components/Input'
import { Button } from 'src/components/Button'
import { useState } from 'react'

import image from './invite_image.svg'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import LoadingSpinner from 'src/components/LoadingSpinner'
import { EventDetails } from './Login'
import dayjs from 'dayjs'

export type Team = {
  id: string
  image: {
    id: string
    filename: string
    full_path: string
    mime_type: string
    width: number
    height: number
  } | null
  name: string
  team_player_roster_id: string
}

export const PlayerSearchTeam = observer(() => {
  const {
    registrationInvite: {
      state: { playerTeams, eventInfo },
      confirmTeamPlayerRosterId,
    },
  } = useMobxStore()
  const [loading, setLoading] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [error, setError] = useState('')

  const { control, handleSubmit } = useForm()

  const submit = (data: any) => {
    setLoading(true)

    const team = playerTeams.find(
      (team) => team.team_player_roster_id === data.roosterId
    )

    if (team) {
      setSelectedTeam(team)
    } else {
      setError('Invitation code not found')
    }

    setLoading(false)
  }

  if (loading) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          flex: 1;
        `}
      >
        <LoadingSpinner size={4} />
      </div>
    )
  }

  if (selectedTeam) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          flex: 1;
        `}
      >
        <img
          src={image}
          css={css`
            width: 195px;
            height: 224px;
            align-self: center;
          `}
        />
        <h1
          css={css`
            color: ${colors.HEADER};
            font-size: 34px;
            font-weight: 700;
            margin-top: 20px;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'Rift, Arial, Helvetica, sans-serif'};
          `}
        >
          You have been invited to the team:
        </h1>
        <div
          css={css`
            background-color: ${colors.LINK_WATER};
            padding: 16px;
            border-radius: 8px;
            margin: 20px 0;
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              margin-bottom: 16px;
            `}
          >
            {selectedTeam.image?.full_path && (
              <img
                src={selectedTeam.image?.full_path}
                css={css`
                  width: 36px;
                  height: 36px;
                  margin-right: 8px;
                `}
              />
            )}
            {selectedTeam?.name && (
              <p
                css={css`
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'Rift, Arial, Helvetica, sans-serif'};
                  font-weight: 700;
                  font-size: 36px;
                  color: #27303e;
                `}
              >
                {selectedTeam?.name}
              </p>
            )}
          </div>
          <EventDetails label='Competition' value={eventInfo?.name} />
          <EventDetails
            label='Organization'
            value={eventInfo?.registration?.organization_name}
          />
          <EventDetails
            label='Dates'
            value={`${dayjs(eventInfo?.registration?.starts_at).format(
              'MMM D'
            )} - ${dayjs(eventInfo?.registration?.ends_at).format(
              'MMM D, YYYY'
            )} `}
          />
          {eventInfo?.registration?.location && (
            <EventDetails label='Location' value={eventInfo?.location} />
          )}
        </div>
        <div
          css={css`
            width: 50%;
          `}
        >
          <Button
            label='Confirm'
            onClick={() => {
              confirmTeamPlayerRosterId(selectedTeam.team_player_roster_id)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        flex: 1;
      `}
    >
      <img
        src={image}
        css={css`
          width: 195px;
          height: 224px;
          align-self: center;
        `}
      />
      <h1
        css={css`
          color: ${colors.HEADER};
          font-size: 34px;
          font-weight: 700;
          margin-top: 20px;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
        `}
      >
        Enter your team invitation code
      </h1>
      <div
        css={css`
          width: 100%;
          margin: 20px 0;
        `}
      >
        <Input
          control={control}
          isLightMode
          fieldName='roosterId'
          required
          placeholder='Enter your team roster ID'
          invalid={error !== ''}
          invalidMessage={error}
        />
      </div>
      <div
        css={css`
          width: 50%;
        `}
      >
        <Button label='Submit' onClick={handleSubmit(submit)} />
      </div>
    </div>
  )
})
